import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Sidebar from "../../sidebar";
import {
  MainContainer,
  Title,
  HeaderSection,
  ContentWrapper,
  StyledButton,
  FormContainer,
  SideContainer,
  ButtonContainerLeft,
  StyledTable,
  PaginationContainer,
  PageButton,
  StyledDatePicker,
  FilterButton,
  loadingWrapperStyle,
  loadingSpinnerStyle,
  loadingTextStyle,
  badgeBaseStyle,
  obsCellEmptyStyle,
  filterBarStyle,
  filterLabelStyle,
  rowsBarStyle,
  rowsLabelStyle,
  rowsSelectStyle,
  resultCountStyle,
  thIndexStyle,
  tdIndexStyle,
  tdCodeStyle,
  tdDateCellStyle,
  tdDateStyle,
  tdTimeStyle,
  tdObsStyle,
  emptyStateStyle,
  emptyStateIconStyle,
  ellipsisStyle,
  pageButtonActiveStyle,
  DropdownWrapper,
  DropdownMenu,
  DropdownItem,
  DesktopOnly,
  MobileCards,
  MobileCard,
  MobileCardTitle,
  MobileCardMeta,
} from "./styles";
import { guardianAlertService } from "../../../services/guardianAlertService";
import { richTextToPlainText } from "../../../utils/richText";
import { openAlertDetails } from "../../../utils/appNavigation";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useModalContext } from "../../CustomModal/ModalProvider.jsx";
import { registerLocale } from "react-datepicker";
import { ptBR } from "date-fns/locale";

registerLocale("pt-BR", ptBR);

// ── Badge de Ação ──────────────────────────────────────────────────────────────
const actionConfig = {
  criado: { label: "Alerta criado", color: "var(--info)", bg: "color-mix(in srgb, var(--info) 15%, transparent)" },
  recebido: { label: "Recebido", color: "var(--accent-secondary)", bg: "var(--accent-secondary-soft)" },
  aguardando_resposta: { label: "Aguardando resposta", color: "var(--warning)", bg: "color-mix(in srgb, var(--warning) 15%, transparent)" },
  resposta_rejeitado: { label: "Resposta Rejeitada", color: "var(--danger)", bg: "color-mix(in srgb, var(--danger) 15%, transparent)" },
  equipe_enviada: { label: "Equipe Enviada", color: "var(--success)", bg: "color-mix(in srgb, var(--success) 15%, transparent)" },
  respondido_processo_sucessao: { label: "Respondido / Processo de Sucessão Natural", color: "var(--accent)", bg: "var(--accent-soft)" },
  encerrado: { label: "Encerrado", color: "var(--text-muted)", bg: "color-mix(in srgb, var(--text-muted) 15%, transparent)" },
};

const ActionBadge = ({ action }) => {
  const cfg = actionConfig[action] || { label: action, color: "var(--text-muted)", bg: "color-mix(in srgb, var(--text-muted) 15%, transparent)" };
  return (
    <span style={{ ...badgeBaseStyle, color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}40` }}>
      {cfg.label}
    </span>
  );
};

// ── Badge de Severidade ────────────────────────────────────────────────────────
const severityConfig = {
  Baixo: { label: "Baixo", color: "var(--accent)", bg: "var(--accent-soft)" },
  Medio: { label: "Médio", color: "var(--warning)", bg: "color-mix(in srgb, var(--warning) 15%, transparent)" },
  Grave: { label: "Grave", color: "var(--danger)", bg: "color-mix(in srgb, var(--danger) 15%, transparent)" },
};


const SeverityBadge = ({ value }) => {
  const cfg = severityConfig[value] || { label: value ?? "-", color: "var(--text-muted)", bg: "color-mix(in srgb, var(--text-muted) 15%, transparent)" };
  return (
    <span style={{ ...badgeBaseStyle, color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}40` }}>
      {cfg.label}
    </span>
  );
};

// ── Célula de Observação com tooltip ──────────────────────────────────────────
const ObsCell = ({ text }) => {
  const plain = richTextToPlainText(text);
  if (!plain || plain === "-") return <span style={obsCellEmptyStyle}>—</span>;
  const truncated = plain.length > 40 ? plain.slice(0, 40) + "…" : plain;
  return (
    <span title={plain} style={{ cursor: plain.length > 40 ? "help" : "default" }}>
      {truncated}
    </span>
  );
};

// ── Componente principal ───────────────────────────────────────────────────────
const AlertHistoryTable = () => {
  const { id_code } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { showConfirm } = useModalContext();


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  const formatDate = (date) => {
    if (!date) return { date: "-", time: "" };
    const d = new Date(date);
    return {
      date: d.toLocaleDateString("pt-BR"),
      time: d.toLocaleTimeString("pt-BR"),
    };
  };

  const safeFormatDate = (date) => {
    if (!date) return "";
    const safeDate = new Date(date);
    safeDate.setHours(12, 0, 0, 0);
    const year = safeDate.getFullYear();
    const month = String(safeDate.getMonth() + 1).padStart(2, "0");
    const day = String(safeDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDownloadPDF = async () => {
    try {
      let firstDate = null;
      if (history.length > 0) {
        const oldest = history[history.length - 1];
        firstDate = oldest.created_at
          ? new Date(oldest.created_at).toLocaleDateString("pt-BR")
          : null;
      }

      const message = (startDate || endDate)
        ? `Deseja gerar o relatório para o período:\nDe: ${startDate || firstDate || "início"}\nAté: ${endDate || new Date().toLocaleDateString("pt-BR")} ?`
        : `Nenhum filtro selecionado.\n\nO relatório será gerado de:\n${firstDate || "primeiro registro"} até ${new Date().toLocaleDateString("pt-BR")}.\n\nDeseja continuar?`;

      const confirmed = await showConfirm("Gerar relatório", message);
      if (!confirmed) return;

      setPdfLoading(true);
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const blob = await guardianAlertService.getAlertReportPDF(id_code, params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const areaSlug = (history[0]?.area_name || "area")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "_");

      const today = new Date().toLocaleDateString("pt-BR").replace(/\//g, "-");
      link.download = `relatorio_${areaSlug}_${today}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      if (error.response?.status === 409) {
        await showConfirm(
          "Relatório já existe",
          "Já existe um relatório gerado para este período. Acesse \"Ver relatórios gerados\" para visualizá-lo."
        );
        return;
      }
      console.error("Erro ao baixar PDF:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const data = await guardianAlertService.getAlertHistoryTable(id_code, params);
      const alerts = Array.isArray(data) ? data : [];
      const formatted = [];

      alerts.forEach((alert) => {
        if (!alert.tickets || alert.tickets.length === 0) {
          formatted.push({
            id: alert.alert_id,
            cod_alerta: alert.cod_alerta,
            area_name: alert.area,
            created_at: alert.created_at,
            action_label: "criado",
            alert_type: alert.alert_type,
            severity: alert.severity,
            created_by_name: alert.created_by,
            observations: alert.observation,
          });
        } else {
          alert.tickets.forEach((ticket) => {
            formatted.push({
              id: ticket.ticket_id,
              ticket_id: ticket.ticket_id,
              cod_alerta: alert.cod_alerta,
              area_name: alert.area,
              created_at: ticket.created_at,
              action_label: ticket.action,
              alert_type: alert.alert_type,
              severity: alert.severity,
              created_by_name: ticket.created_by,
              observations: ticket.observations || alert.observation,
            });
          });
        }
      });

      formatted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setHistory(formatted);
    } catch (error) {
      console.error("Erro ao carregar histórico:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    fetchHistory();
  };

  useEffect(() => {
    if (id_code) fetchHistory();
  }, [id_code]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = history.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(history.length / itemsPerPage);

  if (loading) {
    return (
      <MainContainer>
        <Sidebar />
        <SideContainer>
          <div style={loadingWrapperStyle}>
            <div style={loadingSpinnerStyle} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <span style={loadingTextStyle}>Carregando histórico...</span>
          </div>
        </SideContainer>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Sidebar />
      <SideContainer>
        <ContentWrapper>

          {/* ── Botões topo ── */}
          <ButtonContainerLeft>
            <StyledButton type="button" onClick={() => navigate(id_code ? `/area_details/${id_code}` : "/areas")}>
              ← Voltar
            </StyledButton>
            <DropdownWrapper ref={menuRef}>
              <StyledButton onClick={() => setMenuOpen((open) => !open)} disabled={pdfLoading}>
                {pdfLoading ? "⏳ Gerando..." : "📄 Relatório ▾"}
              </StyledButton>

              {menuOpen && (
                <DropdownMenu>
                  <DropdownItem
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      handleDownloadPDF();
                    }}
                  >
                    📄 Gerar relatório
                  </DropdownItem>
                  <DropdownItem
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate(`/alerts/history-report/${id_code}`);
                    }}
                  >
                    📋 Ver relatórios gerados
                  </DropdownItem>
                </DropdownMenu>
              )}
            </DropdownWrapper>
          </ButtonContainerLeft>

          {/* ── Título ── */}
          <HeaderSection>
            <Title>Histórico de Alertas</Title>
          </HeaderSection>

          {/* ── Filtros de período ── */}
          <div style={filterBarStyle}>
            <span style={filterLabelStyle}>Período:</span>

            <StyledDatePicker
              selected={startDate ? new Date(startDate + "T12:00:00") : null}
              onChange={(date) => setStartDate(safeFormatDate(date))}
              placeholderText="De"
              dateFormat="dd/MM/yyyy"
              locale="pt-BR"
              isClearable
            />

            <StyledDatePicker
              selected={endDate ? new Date(endDate + "T12:00:00") : null}
              onChange={(date) => setEndDate(safeFormatDate(date))}
              placeholderText="Até"
              dateFormat="dd/MM/yyyy"
              locale="pt-BR"
              isClearable
            />

            <FilterButton onClick={() => fetchHistory()}>🔍 Filtrar</FilterButton>
            <FilterButton onClick={handleClearFilter}>❌ Limpar</FilterButton>
          </div>

          {/* ── Por página ── */}
          <div style={rowsBarStyle}>
            <label style={rowsLabelStyle}>Por página:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(e.target.value)}
              style={rowsSelectStyle}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          {/* ── Contagem de resultados ── */}
          {history.length > 0 && (
            <div style={resultCountStyle}>
              {history.length} registro{history.length !== 1 ? "s" : ""} encontrado{history.length !== 1 ? "s" : ""}
            </div>
          )}

          {/* ── Tabela / Cards ── */}
          <FormContainer>
            <DesktopOnly>
            <StyledTable>
              <thead>
                <tr>
                  <th style={thIndexStyle}>#</th>
                  <th>Código</th>
                  <th>Área</th>
                  <th>Data / Hora</th>
                  <th>Ação</th>
                  <th>Tipo</th>
                  <th>Severidade</th>
                  <th>Usuário</th>
                  <th>Observação</th>
                </tr>
              </thead>

              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan="9">
                      <div style={emptyStateStyle}>
                        <span style={emptyStateIconStyle}>🔍</span>
                        <span>Nenhum histórico encontrado para o período selecionado.</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentItems.map((item, index) => {
                    const absoluteIndex = indexOfFirstItem + index + 1;
                    const { date, time } = formatDate(item.created_at);
                    return (
                      <tr key={item.id}>

                        <td style={tdIndexStyle}>{absoluteIndex}</td>

                        <td>
                          {item.cod_alerta ? (
                            <span
                              style={tdCodeStyle}
                              onClick={() => {
                                if (item.ticket_id) {
                                  openAlertDetails(dispatch, navigate, {
                                    ticketId: item.ticket_id,
                                    codAlerta: item.cod_alerta,
                                  });
                                  return;
                                }
                                navigate(`/alerts/buscar/${item.cod_alerta}`);
                              }}
                            >
                              {item.cod_alerta}
                            </span>
                          ) : "-"}
                        </td>

                        <td>{item.area_name || "-"}</td>

                        <td>
                          <div style={tdDateCellStyle}>
                            <span style={tdDateStyle}>{date}</span>
                            <span style={tdTimeStyle}>{time}</span>
                          </div>
                        </td>

                        <td><ActionBadge action={item.action_label} /></td>

                        <td>{item.alert_type || "-"}</td>

                        <td><SeverityBadge value={item.severity} /></td>

                        <td>{item.created_by_name || "-"}</td>

                        <td style={tdObsStyle}>
                          <ObsCell text={item.observations} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </StyledTable>
            </DesktopOnly>

            <MobileCards>
              {history.length === 0 ? (
                <MobileCard>
                  <MobileCardMeta>
                    Nenhum histórico encontrado para o período selecionado.
                  </MobileCardMeta>
                </MobileCard>
              ) : (
                currentItems.map((item, index) => {
                  const absoluteIndex = indexOfFirstItem + index + 1;
                  const { date, time } = formatDate(item.created_at);
                  return (
                    <MobileCard key={`m-${item.id}`}>
                      <MobileCardTitle>
                        #{absoluteIndex}
                        {item.cod_alerta ? (
                          <>
                            {" · "}
                            <span
                              style={tdCodeStyle}
                              onClick={() => {
                                if (item.ticket_id) {
                                  openAlertDetails(dispatch, navigate, {
                                    ticketId: item.ticket_id,
                                    codAlerta: item.cod_alerta,
                                  });
                                  return;
                                }
                                navigate(`/alerts/buscar/${item.cod_alerta}`);
                              }}
                            >
                              {item.cod_alerta}
                            </span>
                          </>
                        ) : null}
                      </MobileCardTitle>
                      <MobileCardMeta>{item.area_name || "-"}</MobileCardMeta>
                      <MobileCardMeta>
                        {date} {time}
                      </MobileCardMeta>
                      <div>
                        <ActionBadge action={item.action_label} />
                      </div>
                      <MobileCardMeta>
                        {item.alert_type || "-"}
                      </MobileCardMeta>
                      <div>
                        <SeverityBadge value={item.severity} />
                      </div>
                      <MobileCardMeta>
                        Por: {item.created_by_name || "-"}
                      </MobileCardMeta>
                      <div style={tdObsStyle}>
                        <ObsCell text={item.observations} />
                      </div>
                    </MobileCard>
                  );
                })
              )}
            </MobileCards>

            {/* ── Paginação ── */}
            {totalPages > 1 && (
              <PaginationContainer>
                <PageButton
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  <FiChevronLeft />
                </PageButton>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === "..." ? (
                      <span key={`ellipsis-${i}`} style={ellipsisStyle}>…</span>
                    ) : (
                      <PageButton
                        key={p}
                        type="button"
                        onClick={() => setCurrentPage(p)}
                        style={p === currentPage ? pageButtonActiveStyle : {}}
                      >
                        {p}
                      </PageButton>
                    )
                  )}

                <PageButton
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  <FiChevronRight />
                </PageButton>
              </PaginationContainer>
            )}

          </FormContainer>

        </ContentWrapper>
      </SideContainer>
    </MainContainer>
  );
};

export default AlertHistoryTable;